import { Link } from "@trussworks/react-uswds";
import { useTranslation } from "react-i18next";

import { ReactComponent as Telephone } from "../../images/phone.svg";
import { ReactComponent as Website } from "../../images/website.svg";
import { ReactComponent as Location } from "../../images/location.svg";
import { ReactComponent as DollarSign } from "../../images/dollar-sign.svg";
import { ReactComponent as Clock } from "../../images/clock.svg";
import { ReactComponent as BadgeCheck } from "../../images/badge-check.svg";
import { ReactComponent as Telehealth } from "../../images/telehealth.svg";
import { ReactComponent as Calendar } from "../../images/calendar.svg";
import { ReactComponent as Lifts } from "../../images/logos/lifts_logo.svg";
import ResultDatum from "./ResultDatum";
import { V1Clinic, ClinicLocation, ClinicPhone } from "../../types/v1_schemas";
import WebsiteLink from "./WebsiteLink";
import { Fragment } from "react";
import Badge from "../Badge";
import { Grid } from "@trussworks/react-uswds";
import ProviderLicenseCard from "../Search/ProviderLicenseCard";
import { isMobile } from "react-device-detect";
type BasicResultDetailV1Props = {
  result: V1Clinic | undefined;
  isCondensed?: boolean;
};

function PhoneSort(a: ClinicPhone, b: ClinicPhone): number {
  // -1 for a < b (a goes first), 0 for a = b, 1 for a > b (b goes first)
  //desired order: Front desk (Recepción) > Crisis line (Línea de crisis) > Scheduling (Programación de citas) > Other (Otros)
  if (a.phoneNumberTypeText) {
    if (!b.phoneNumberTypeText) return -1; // a has text, b doesn't

    if (
      a.phoneNumberTypeText.includes("Front desk") ||
      a.phoneNumberTypeText.includes("Recepción")
    ) {
      if (
        b.phoneNumberTypeText.includes("Front desk") ||
        b.phoneNumberTypeText.includes("Recepción")
      )
        return 0;
      else return -1;
    } else if (
      a.phoneNumberTypeText.includes("Scheduling") ||
      a.phoneNumberTypeText.includes("Programación de citas")
    ) {
      if (
        b.phoneNumberTypeText.includes("Front desk") ||
        b.phoneNumberTypeText.includes("Recepción")
      )
        return 1;
      else if (
        b.phoneNumberTypeText.includes("Scheduling") ||
        b.phoneNumberTypeText.includes("Programación de citas")
      )
        return 0;
      else return -1;
    } else if (
      a.phoneNumberTypeText.includes("Crisis line") ||
      a.phoneNumberTypeText.includes("Línea de crisis")
    ) {
      if (
        b.phoneNumberTypeText.includes("Front desk") ||
        b.phoneNumberTypeText.includes("Recepción") ||
        b.phoneNumberTypeText.includes("Scheduling") ||
        b.phoneNumberTypeText.includes("Programación de citas")
      )
        return 1;
      else if (
        b.phoneNumberTypeText.includes("Crisis line") ||
        b.phoneNumberTypeText.includes("Línea de crisis")
      )
        return 0;
      else return -1;
    } else {
      if (
        b.phoneNumberTypeText.includes("Front desk") ||
        b.phoneNumberTypeText.includes("Recepción") ||
        b.phoneNumberTypeText.includes("Crisis line") ||
        b.phoneNumberTypeText.includes("Línea de crisis") ||
        b.phoneNumberTypeText.includes("Scheduling") ||
        b.phoneNumberTypeText.includes("Programación de citas")
      )
        return 1;
      else return 0;
    }
  } else {
    if (b.phoneNumberTypeText) return 1; // b has text, a doesn't
    else return 0; // neither a nor b has text
  }
}

function BasicResultDetailV1({
  result,
  isCondensed,
}: BasicResultDetailV1Props) {
  //console.log(">>>>>> IN BasicResultDetail.tsx >>>>>");
  const { t } = useTranslation();

  let v1ClinicLocation = result?.clinicLocations?.find(
    (element: ClinicLocation) =>
      element.latitude?.length && element.longitude?.length
  );
  if (!v1ClinicLocation) {
    // eslint-disable-next-line unused-imports/no-unused-vars
    v1ClinicLocation = result?.clinicLocations?.find(
      // eslint-disable-next-line unused-imports/no-unused-vars
      (element: ClinicLocation) =>
        result?.clinicLocations?.length ? result.clinicLocations[0] : undefined
    );
  }

  let position = null;

  if (
    v1ClinicLocation &&
    v1ClinicLocation.latitude?.length &&
    v1ClinicLocation.longitude?.length
  ) {
    let lat = +v1ClinicLocation.latitude;
    //console.log(`lat is ${lat}`);
    let lng = +v1ClinicLocation.longitude;
    //console.log(`lng is ${lng}`);
    position = { lat: lat, lng: lng };
  }

  let address: Array<string> = [];

  if (
    v1ClinicLocation &&
    v1ClinicLocation.addressLine1?.length &&
    v1ClinicLocation.cityText?.length &&
    v1ClinicLocation.postalCode?.length
  ) {
    if (isCondensed) {
      address = [
        v1ClinicLocation.cityText + ", CO " + v1ClinicLocation.postalCode,
      ];
    } else {
      address = [
        v1ClinicLocation.addressLine1,
        v1ClinicLocation.cityText + ", CO " + v1ClinicLocation.postalCode,
      ];
    }
  }

  let telehealthAvailable = false;

  if (result?.clinicTelehealthOption?.lineageText !== "No Telehealth Available" ) {
    telehealthAvailable = true;
  }

  let telehealthOnly = false;

  if (result?.clinicTelehealthOption?.lineageText === "Telehealth Only" ) {
    telehealthOnly = true;
  }

  let pleaseCallUs: boolean =
    result?.hoursOfOperationMonday?.toLowerCase() === "closed" &&
    result?.hoursOfOperationTuesday?.toLowerCase() === "closed" &&
    result?.hoursOfOperationWednesday?.toLowerCase() === "closed" &&
    result?.hoursOfOperationThursday?.toLowerCase() === "closed" &&
    result?.hoursOfOperationFriday?.toLowerCase() === "closed" &&
    result?.hoursOfOperationSaturday?.toLowerCase() === "closed" &&
    result?.hoursOfOperationSunday?.toLowerCase() === "closed";

  if (!pleaseCallUs) {
    pleaseCallUs =
      result?.hoursOfOperationMonday?.toLowerCase() === "cerrado" &&
      result?.hoursOfOperationTuesday?.toLowerCase() === "cerrado" &&
      result?.hoursOfOperationWednesday?.toLowerCase() === "cerrado" &&
      result?.hoursOfOperationThursday?.toLowerCase() === "cerrado" &&
      result?.hoursOfOperationFriday?.toLowerCase() === "cerrado" &&
      result?.hoursOfOperationSaturday?.toLowerCase() === "cerrado" &&
      result?.hoursOfOperationSunday?.toLowerCase() === "cerrado";
  }

  return (
    <Grid>
      {!isCondensed && (
        <div
          style={{
            overflow: "hidden",
            textOverflow: "clip",
            whiteSpace: "normal",
            color: '#4F4F4F'
          }}
          className="margin-top-3 print-no-margin"
        >
          {result?.description}
          <ProviderLicenseCard
            isBhaLicensed={result?.bhaProvider ?? false}
            isMobile={isMobile}
          ></ProviderLicenseCard>
        </div>
      )}
      <div className="margin-top-0 margin-bottom-1 print-no-margin">
        <Grid row mobile={{ col: true }} style={{ gap: "12px", color:'#4F4F4F' }} className="">
          {((!!address?.length && !!position) || telehealthOnly) && (
            <ResultDatum
              Icon={
                !isCondensed
                  ? address?.length
                    ? Location
                    : Telehealth
                  : undefined
              }
              iconClassName={"margin-top-05"}
              className={isCondensed ? "margin-y-0" : ""}
              key="address"
            >
              <h3 className="usa-sr-only">{t("address")}</h3>
              <div className="dark_blue" >
                {address?.length //&& position   Commented this line to address AZ296719. One particular clinic has blank address with lat/long.
                  ? address.map((addr, idx) => (
                      <Fragment key={idx}>
                        {addr}
                        <br />
                      </Fragment>
                    ))
                  : t("telehealthOnlyAddress")}
              </div>
            </ResultDatum>
          )}
          {(result?.newPatientAcceptance ||
            telehealthAvailable ||
            !result?.appointmentRequired ||
            result?.bhasoNetwork) && (
            <div className="print-no-margin display-flex flex-wrap">
              {telehealthAvailable && (
                <Badge
                  bgColor="yellow"
                  Icon={<Telehealth height={13} />}
                  text={t("telehealthAvailable")}
                  showTooltip
                  tooltipText={t("telehealthAvailableNote")}
                />
              )}
              {result?.newPatientAcceptance && (
                <Badge
                  bgColor="blue"
                  Icon={<BadgeCheck />}
                  text={t("acceptingNewPatients")}
                  showTooltip
                  tooltipText={t("acceptingNewPatientsNote")}
                />
              )}
              {!(result?.appointmentRequired ?? true) && (
                <Badge
                  bgColor="navy"
                  Icon={<Calendar height={13} />}
                  text={t("noAppointment")}
                />
              )}
              {result?.bhasoNetwork && isCondensed && (
                <Badge
                  bgColor="navy"
                  Icon={<Lifts height="13"/>}
                  text={t("liftsProvider")}
                  showTooltip
                  tooltipText={t("liftsProviderTooltip")}
                />
              )}
            </div>
          )}
        </Grid>

        {!isCondensed && result?.clinicPhones?.length && (
          <ResultDatum
            Icon={!isCondensed ? Telephone : undefined}
            iconClassName="margin-top-05"
            className={isCondensed ? "margin-y-0" : ""}
            key="telephone"
          >
            {result.clinicPhones.sort(PhoneSort).map((phones, idx) =>
              !isCondensed || idx === 0 ? (
                <div key={idx} className="dark_blue">
                  <h3 className="usa-sr-only">{t("telephoneNumber")}</h3>
                  <Link variant="external" href={`tel:${phones.phone}`} className="" style={{color: '#1E6692'}}>
                    {phones.phone}
                  </Link>
                  {!isCondensed ? " | " + phones.phoneNumberTypeText : ""}
                </div>
              ) : (
                <></>
              )
            )}
          </ResultDatum>
        )}
        {!isCondensed && result?.website && (
          <ResultDatum
            Icon={!isCondensed ? Website : undefined}
            className={isCondensed ? "margin-y-0" : ""}
            key="website"
          >
            <h3 className="usa-sr-only" style={{color:'#1E6692'}}>{t("website")}</h3>
            <WebsiteLink url={result.website} />
          </ResultDatum>
        )}
      </div>

      {!isCondensed ? (
        <ResultDatum Icon={DollarSign} key="fees">
          {
            <h3 className="font-body-sm margin-top-0 margin-bottom-05 dark_blue">
              <span className="dark_blue">{t("feesTitle")}</span>
            </h3>
          }
          {result &&
          result.clinicPaymentOptions &&
          result.clinicPaymentOptions.length > 0 ? (
            <p className="dark_blue">
              {t("feeAcceptText") +
                " " +
                result.clinicPaymentOptions
                  .map((x) => (x.paymentTypeDescription?.toLowerCase() === "other" 
                               ||
                               x.paymentTypeDescription?.toLowerCase() === "otro") ? x.paymentTypeWriteIn : x.paymentTypeDescription)
                  .join(", ")}
            </p>
          ) : (
            <p className="dark_blue">{t("moreInfo")}</p>
          )}
        </ResultDatum>
      ) : (
        result &&
        result.clinicPaymentOptions &&
        result.clinicPaymentOptions.length > 0 && (
          <ResultDatum Icon={DollarSign} key="fees">
            {result &&
            result.clinicPaymentOptions &&
            result.clinicPaymentOptions.length > 0 ? (
              <>
                {t("feeAcceptText") +
                  " " +
                  result.clinicPaymentOptions
                    .map((x) => (x.paymentTypeDescription?.toLowerCase() === "other" 
                    ||
                    x.paymentTypeDescription?.toLowerCase() === "otro") ? x.paymentTypeWriteIn : x.paymentTypeDescription)
                    .join(", ")}
              </>
            ) : (
              <>{t("moreInfo")}</>
            )}
          </ResultDatum>
        )
      )}

      {!isCondensed && (
        <ResultDatum Icon={Clock} key="hours">
          <h3 className="font-body-sm margin-top-0 margin-bottom-05">
            <span className="dark_blue">{t("hours")}</span>
          </h3>

          {pleaseCallUs && <span className="dark_blue">{t("moreInfo")}</span>}
          {!pleaseCallUs && (
            <Fragment>
              <div className="margin-top-05 dark_blue" key={"Monday"} >
                {t("hoursValuesmonday")}
                {result?.hoursOfOperationMonday?.length
                  ? ` ${result.hoursOfOperationMonday === "Open 24 hours" || result.hoursOfOperationMonday === "Open24Hours" ? t("open24Hours") : result.hoursOfOperationMonday}`
                  : ""}
              </div>
              <div className="margin-top-05 dark_blue" key={"Tuesday"}>
                {t("hoursValuestuesday")}
                {result?.hoursOfOperationTuesday?.length
                  ? ` ${result.hoursOfOperationTuesday === "Open 24 hours" || result.hoursOfOperationTuesday === "Open24Hours" ? t("open24Hours") : result.hoursOfOperationTuesday}`
                  : ""}
              </div>
              <div className="margin-top-05 dark_blue" key={"Wednesday"}>
                {t("hoursValueswednesday")}
                {result?.hoursOfOperationWednesday?.length
                  ? ` ${result.hoursOfOperationWednesday === "Open 24 hours" || result.hoursOfOperationWednesday === "Open24Hours" ? t("open24Hours") : result.hoursOfOperationWednesday}`
                  : ""}
              </div>
              <div className="margin-top-05 dark_blue" key={"Thursday"}>
                {t("hoursValuesthursday")}
                {result?.hoursOfOperationThursday?.length
                  ? ` ${result.hoursOfOperationThursday === "Open 24 hours" || result.hoursOfOperationThursday === "Open24Hours" ? t("open24Hours") : result.hoursOfOperationThursday}`
                  : ""}
              </div>
              <div className="margin-top-05 dark_blue" key={"Friday"}>
                {t("hoursValuesfriday")}
                {result?.hoursOfOperationFriday?.length
                  ? ` ${result.hoursOfOperationFriday === "Open 24 hours" || result.hoursOfOperationFriday === "Open24Hours" ? t("open24Hours") : result.hoursOfOperationFriday}`
                  : ""}
              </div>
              <div className="margin-top-05 dark_blue" key={"Saturday"}>
                {t("hoursValuessaturday")}
                {result?.hoursOfOperationSaturday?.length
                  ? ` ${result.hoursOfOperationSaturday === "Open 24 hours" || result.hoursOfOperationSaturday === "Open24Hours" ? t("open24Hours") : result.hoursOfOperationSaturday}`
                  : ""}
              </div>
              <div className="margin-top-05 dark_blue" key={"Sunday"}>
                {t("hoursValuessunday")}
                {result?.hoursOfOperationSunday?.length
                  ? ` ${result.hoursOfOperationSunday === "Open 24 hours" || result.hoursOfOperationSunday === "Open24Hours" ? t("open24Hours") : result.hoursOfOperationSunday}`
                  : ""}
              </div>
            </Fragment>
          )}
        </ResultDatum>
      )}
    </Grid>
  );
}

export default BasicResultDetailV1;
